import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Plus, X } from "lucide-react";
import {
  type KeyboardEvent as ReactKeyboardEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import type {
  ActivityDetail,
  CompletionReflectionResponse,
  ActivitySession,
  ReflectionSessionOperation,
  PlanningDateChoice,
} from "@learn-flow/contracts";

import { ApiError } from "../../lib/api.js";
import { createUuid } from "../../lib/random-uuid.js";
import { addLocalDays } from "../calendar/calendar-date.js";
import { calendarQueryKey, loadCalendar } from "../calendar/calendar-api.js";
import { libraryQueryRoot } from "../library/library-api.js";
import {
  activityDetailKey,
  loadSessionPlanningOptions,
  saveCompletionReflection,
} from "./activity-api.js";
import {
  previousReflectionCalendarStartDate,
  reflectionCalendarStartDate,
} from "./completion-reflection-calendar.js";
import { CompletionReflectionWeekPlanner } from "./completion-reflection-week-planner.js";

type SessionDraft = {
  key: string;
  sessionId: string | null;
  version: number | null;
  idempotencyKey: string;
  scheduledDate: string;
  scheduledTime: string;
  original: {
    scheduledDate: string;
    scheduledTime: string;
  } | null;
  editing: boolean;
  removed: boolean;
};

type SessionPlannerDraft = {
  choice: PlanningDateChoice;
  calendarStartDate: string;
  scheduledDate: string;
  scheduledTime: string;
};

export function CompletionReflectionDialog({
  activity,
  completionId,
  onClose,
}: {
  activity: ActivityDetail;
  completionId: string;
  onClose: () => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const requestKey = useRef(createUuid());
  const [difficulty, setDifficulty] = useState(activity.difficulty?.toString() ?? "");
  const [interest, setInterest] = useState(activity.interest?.toString() ?? "");
  const [rows, setRows] = useState<SessionDraft[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [message, setMessage] = useState("");
  const [planner, setPlanner] = useState<SessionPlannerDraft | null>(null);
  const options = useQuery({
    queryKey: ["completion-reflection-options", activity.id, completionId],
    queryFn: () => loadSessionPlanningOptions(activity.id, completionId),
  });
  const plannerCalendarStartDate = planner?.calendarStartDate ?? null;
  const plannerCalendar = useQuery({
    queryKey: calendarQueryKey(plannerCalendarStartDate),
    queryFn: () => loadCalendar(plannerCalendarStartDate),
    enabled: planner !== null,
  });

  useLayoutEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    headingRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    if (!options.data || initialized) return;
    setRows(
      activity.sessions
        .filter(
          (session) =>
            session.status === "scheduled" && session.scheduledDate >= options.data.today,
        )
        .map(sessionDraftFromSession),
    );
    setInitialized(true);
  }, [activity.sessions, initialized, options.data]);

  const save = useMutation({
    mutationFn: () =>
      saveCompletionReflection(activity.id, completionId, {
        idempotencyKey: requestKey.current,
        difficulty: Number(difficulty),
        interest: Number(interest),
        sessionOperations: buildOperations(rows),
      }),
    onSuccess: async (result: CompletionReflectionResponse) => {
      requestKey.current = createUuid();
      setDifficulty(result.ratingEvent.difficulty.toString());
      setInterest(result.ratingEvent.interest.toString());
      setRows(result.futureSessions.map(sessionDraftFromSession));
      setPlanner(null);
      setMessage(
        result.replayed
          ? "This reflection and session plan was already saved."
          : "Reflection and session plan saved.",
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: activityDetailKey(activity.id) }),
        queryClient.invalidateQueries({ queryKey: libraryQueryRoot }),
        queryClient.invalidateQueries({ queryKey: ["today"] }),
        queryClient.invalidateQueries({ queryKey: ["calendar"] }),
      ]);
    },
    onError: () => {
      setMessage("");
      window.setTimeout(() => errorRef.current?.focus(), 0);
    },
  });

  const planningOptions = options.data;
  const invalid =
    !difficulty ||
    !interest ||
    rows.some(
      (row) =>
        !row.removed &&
        (!row.scheduledDate ||
          (planningOptions !== undefined && row.scheduledDate < planningOptions.today)),
    );
  const visibleRows = rows.filter((row) => !row.removed);
  const addSession = (scheduledDate: string, scheduledTime: string) => {
    setMessage("");
    setRows((current) => [
      ...current,
      {
        key: createUuid(),
        sessionId: null,
        version: null,
        idempotencyKey: createUuid(),
        scheduledDate,
        scheduledTime,
        original: null,
        editing: true,
        removed: false,
      },
    ]);
  };
  const openPlanner = (choice: PlanningDateChoice, today: string, scheduledDate: string): void => {
    setMessage("");
    setPlanner({
      choice,
      calendarStartDate: reflectionCalendarStartDate(choice, today, scheduledDate),
      scheduledDate,
      scheduledTime: "",
    });
  };
  const removeSession = (key: string): void => {
    const index = visibleRows.findIndex((row) => row.key === key);
    const focusKey = visibleRows[index + 1]?.key ?? visibleRows[index - 1]?.key;
    setMessage("");
    setRows((current) =>
      current.map((candidate) =>
        candidate.key === key ? { ...candidate, removed: true } : candidate,
      ),
    );
    window.setTimeout(() => {
      const target = focusKey
        ? dialogRef.current?.querySelector<HTMLButtonElement>(
            `[data-session-remove-key="${focusKey}"]`,
          )
        : dialogRef.current?.querySelector<HTMLButtonElement>(".reflection-quick-add button");
      target?.focus();
    }, 0);
  };

  return (
    <div className="reflection-dialog-backdrop">
      <div
        ref={dialogRef}
        className={`reflection-dialog ${planner ? "has-week-planner" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reflection-dialog-heading"
        aria-describedby="reflection-dialog-description"
        onKeyDown={(event) => handleDialogKeyDown(event, dialogRef.current, onClose)}
      >
        <button
          type="button"
          className="reflection-dialog-close"
          aria-label="Close reflection"
          onClick={onClose}
        >
          <X size={18} aria-hidden="true" />
        </button>
        <span className="section-kicker">Completion reflection</span>
        <h2 id="reflection-dialog-heading" ref={headingRef} tabIndex={-1}>
          Capture how it felt and plan sessions
        </h2>
        <p id="reflection-dialog-description">
          Your completion is already saved. Reflection and future planning are optional.
        </p>

        <fieldset className="reflection-ratings">
          <legend>How did this learning feel?</legend>
          <RatingControl
            label="Difficulty"
            value={difficulty}
            onChange={(value) => {
              setDifficulty(value);
              setMessage("");
            }}
          />
          <RatingControl
            label="Interest"
            value={interest}
            onChange={(value) => {
              setInterest(value);
              setMessage("");
            }}
          />
        </fieldset>

        <section className="reflection-sessions" aria-labelledby="reflection-sessions-heading">
          <div className="reflection-section-heading">
            <div>
              <h3 id="reflection-sessions-heading">Future sessions</h3>
              <p>Add as many as you need. Untimed sessions are welcome.</p>
            </div>
            <CalendarClock size={20} aria-hidden="true" />
          </div>
          {options.isPending ? (
            <p className="reflection-loading" aria-live="polite">
              Loading session dates…
            </p>
          ) : options.isError || !planningOptions ? (
            <div className="reflection-message error" role="alert">
              Session dates could not be loaded. Close this window and try again.
            </div>
          ) : (
            <>
              <div className="reflection-quick-add" aria-label="Add a session">
                {planningOptions.choices.map((choice) => (
                  <button
                    key={choice.choice}
                    type="button"
                    className="soft-button"
                    aria-pressed={planner?.choice === choice.choice}
                    onClick={() =>
                      openPlanner(choice.choice, planningOptions.today, choice.scheduledDate)
                    }
                  >
                    <Plus size={14} aria-hidden="true" /> {choice.label}
                  </button>
                ))}
                <button
                  type="button"
                  className="soft-button"
                  aria-pressed={planner?.choice === "custom"}
                  onClick={() => openPlanner("custom", planningOptions.today, "")}
                >
                  <Plus size={14} aria-hidden="true" /> Custom
                </button>
              </div>
              <p className="timezone-note">
                Dates use {planningOptions.timeZone}.
                {planningOptions.usedFallbackTimeZone
                  ? " Your saved timezone is unavailable, so UTC is being used."
                  : ""}
              </p>
            </>
          )}

          {planner && planningOptions ? (
            <CompletionReflectionWeekPlanner
              calendar={plannerCalendar.data}
              choice={planner.choice}
              isError={plannerCalendar.isError}
              isFetching={plannerCalendar.isFetching}
              isPending={plannerCalendar.isPending}
              scheduledDate={planner.scheduledDate}
              scheduledTime={planner.scheduledTime}
              today={planningOptions.today}
              onAdd={() => {
                if (!planner.scheduledDate || planner.scheduledDate < planningOptions.today) return;
                addSession(planner.scheduledDate, planner.scheduledTime);
                setPlanner(null);
              }}
              onCancel={() => setPlanner(null)}
              onChangeDate={(scheduledDate) =>
                setPlanner((current) => (current ? { ...current, scheduledDate } : current))
              }
              onChangeTime={(scheduledTime) =>
                setPlanner((current) => (current ? { ...current, scheduledTime } : current))
              }
              onPrevious={() =>
                setPlanner((current) =>
                  current
                    ? {
                        ...current,
                        calendarStartDate: previousReflectionCalendarStartDate(
                          current.calendarStartDate,
                          planningOptions.today,
                        ),
                      }
                    : current,
                )
              }
              onNext={() =>
                setPlanner((current) =>
                  current
                    ? {
                        ...current,
                        calendarStartDate: addLocalDays(current.calendarStartDate, 7),
                      }
                    : current,
                )
              }
              onRetry={() => void plannerCalendar.refetch()}
            />
          ) : null}

          {initialized && visibleRows.length === 0 && !planner ? (
            <p className="reflection-empty">
              No future sessions planned. You can leave it that way.
            </p>
          ) : null}
          <ul className="reflection-session-list">
            {visibleRows.map((row, index) => (
              <SessionRow
                key={row.key}
                row={row}
                index={index}
                today={planningOptions?.today ?? ""}
                activityDurationMinutes={activity.estimatedMinutes}
                disabled={save.isPending}
                onChange={(next) => {
                  setMessage("");
                  setRows((current) =>
                    current.map((candidate) => (candidate.key === next.key ? next : candidate)),
                  );
                }}
                onRemove={() => removeSession(row.key)}
              />
            ))}
          </ul>
        </section>

        {save.isError ? (
          <div ref={errorRef} className="reflection-message error" role="alert" tabIndex={-1}>
            <strong>Nothing in this reflection plan was changed.</strong>
            <span>{reflectionErrorMessage(save.error)}</span>
          </div>
        ) : null}
        {message ? (
          <p className="reflection-message success" role="status" aria-live="polite">
            {message}
          </p>
        ) : null}

        <div className="reflection-dialog-actions">
          <button
            type="button"
            className="primary-button"
            disabled={invalid || options.isError || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Saving…" : "Save reflection & session plan"}
          </button>
          <button type="button" className="soft-button" disabled={save.isPending} onClick={onClose}>
            Done for now
          </button>
        </div>
      </div>
    </div>
  );
}

function RatingControl({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const name = `reflection-${label.toLowerCase()}`;
  return (
    <fieldset className="reflection-rating-control">
      <legend>{label}</legend>
      <div>
        {[1, 2, 3, 4, 5].map((rating) => (
          <label key={rating}>
            <input
              type="radio"
              name={name}
              value={rating}
              checked={value === rating.toString()}
              onChange={() => onChange(rating.toString())}
            />
            <span>{rating}</span>
            <span className="visually-hidden"> out of 5</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function SessionRow({
  row,
  index,
  today,
  activityDurationMinutes,
  disabled,
  onChange,
  onRemove,
}: {
  row: SessionDraft;
  index: number;
  today: string;
  activityDurationMinutes: number;
  disabled: boolean;
  onChange: (row: SessionDraft) => void;
  onRemove: () => void;
}) {
  const dateRef = useRef<HTMLInputElement>(null);
  const beginChange = () => {
    onChange({ ...row, editing: true });
    window.setTimeout(() => dateRef.current?.focus(), 0);
  };
  return (
    <li>
      <div className="reflection-session-row-heading">
        <strong>Session {index + 1}</strong>
      </div>
      {row.editing ? (
        <div className="reflection-session-fields">
          <label>
            <span>Date</span>
            <input
              ref={dateRef}
              type="date"
              required
              min={today || undefined}
              value={row.scheduledDate}
              onChange={(event) => onChange({ ...row, scheduledDate: event.target.value })}
            />
          </label>
          <label>
            <span>
              Time <i>Optional</i>
            </span>
            <input
              type="time"
              value={row.scheduledTime}
              onChange={(event) => onChange({ ...row, scheduledTime: event.target.value })}
            />
          </label>
          <p>{activityDurationMinutes} minute activity</p>
        </div>
      ) : (
        <p>
          {formatLocalDate(row.scheduledDate)}
          {row.scheduledTime ? ` at ${row.scheduledTime}` : ", untimed"} · {activityDurationMinutes}{" "}
          min
        </p>
      )}
      <div className="reflection-session-actions">
        <button type="button" disabled={disabled} onClick={beginChange}>
          Change
        </button>
        <button
          type="button"
          data-session-remove-key={row.key}
          disabled={disabled}
          onClick={onRemove}
        >
          Remove
        </button>
      </div>
    </li>
  );
}

function sessionDraftFromSession(session: ActivitySession): SessionDraft {
  const scheduledTime = session.scheduledTime ?? "";
  return {
    key: session.id,
    sessionId: session.id,
    version: session.version,
    idempotencyKey: createUuid(),
    scheduledDate: session.scheduledDate,
    scheduledTime,
    original: {
      scheduledDate: session.scheduledDate,
      scheduledTime,
    },
    editing: false,
    removed: false,
  };
}

function buildOperations(rows: SessionDraft[]): ReflectionSessionOperation[] {
  return rows.flatMap((row): ReflectionSessionOperation[] => {
    if (row.sessionId && row.version) {
      if (row.removed) {
        return [{ action: "remove", sessionId: row.sessionId, version: row.version }];
      }
      if (
        row.original &&
        row.original.scheduledDate === row.scheduledDate &&
        row.original.scheduledTime === row.scheduledTime
      ) {
        return [];
      }
      return [
        {
          action: "change",
          sessionId: row.sessionId,
          version: row.version,
          scheduledDate: row.scheduledDate,
          scheduledTime: row.scheduledTime || null,
        },
      ];
    }
    if (row.removed) return [];
    return [
      {
        action: "add",
        idempotencyKey: row.idempotencyKey,
        scheduledDate: row.scheduledDate,
        scheduledTime: row.scheduledTime || null,
      },
    ];
  });
}

function handleDialogKeyDown(
  event: ReactKeyboardEvent<HTMLDivElement>,
  dialog: HTMLDivElement | null,
  dismiss: () => void,
): void {
  if (event.key === "Escape") {
    event.preventDefault();
    dismiss();
    return;
  }
  if (event.key !== "Tab" || !dialog) return;
  const focusable = [
    ...dialog.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    ),
  ].filter((element) => element.getClientRects().length > 0);
  const first = focusable[0];
  const last = focusable.at(-1);
  if (!first || !last) return;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function reflectionErrorMessage(error: Error): string {
  if (error instanceof ApiError) {
    const firstFieldError = Object.values(error.problem?.fieldErrors ?? {}).flat()[0];
    return firstFieldError ?? error.problem?.title ?? "Check the fields and try again.";
  }
  return "Your choices are still here. Try saving again.";
}

function formatLocalDate(value: string): string {
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return value;
  return new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, day)));
}
